import { ErrorHandler, Injectable, NgZone } from '@angular/core';
import { Http } from '@angular/http';
import { AppVersion } from '@ionic-native/app-version';
import { File as NativeFile } from '@ionic-native/file';
import { FileOpener } from '@ionic-native/file-opener';
import { FileTransfer } from '@ionic-native/file-transfer';
import { Actions } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { AlertController, LoadingController, Platform, PopoverController, ToastController } from 'ionic-angular';
import { Subject } from 'rxjs';
import { Observable } from 'rxjs/Observable';
import { of } from 'rxjs/observable/of';
import { AppConfig } from '../../app/app.config';
import { CordovaThumbnailService } from '../../app/services/cordova-thumbnail.service';
import { RemoteThumbnailService } from '../../app/services/remote-thumbnail.service';
import { ApiFacade } from '../../core/facades/api-facade';
import { NetworkFacade } from '../../core/facades/network-facade';
import { SessionFacade } from '../../core/facades/session-facade';
import { StateManagerFacade } from '../../core/facades/state-manager-facade';
import { SyncUpFacade } from '../../core/facades/sync-up-facade';
import { VASRestService } from '../../core/services/api/vas-rest';
import { IAppFacade } from '../../core/services/app-facade/app-facade.interface';
import { AppFacade } from '../../core/services/app-facade/app-facade.service';
import { NormalizationService } from '../../core/services/normalization/normalization.service';
import { ScheduledTask } from '../../core/services/scheduler/scheduled-task';
import { SchedulerService } from '../../core/services/scheduler/scheduler.service';
import { IStorage } from '../../core/services/storage/storage.interface';
import { ObjectsStoreFacade } from '../../core/store/facade/objects-store-facade';
import { AppReadyAction, BootedSystemEvent, BootingSystemEvent } from '../../core/system/system.actions';
import * as fromConfig from '../business-objects/config/config.actions';
import { ConfigQueries } from '../business-objects/config/config.queries';
import { User } from '../business-objects/user/user.model';
import { AuthFacade } from '../facades/auth-facade';
import { ChangeLogFacade } from '../facades/change-log-facade';
import { ObjectsFacade } from '../facades/objects.facade';
import { SyncDownFacade } from '../facades/sync-down-facade';
import { VasStore } from '../store/store-type';
import { ShareService } from './share/share.service';
import { UniversalFilesystemService } from './universal-filesystem/universal-filesystem.service';
import { ENV } from '../../environments/environment';

@Injectable()
export class VasAppFacade extends AppFacade implements IAppFacade {

    static _instance: VasAppFacade;

    private hasBooted: boolean = false;
    private isBooting: boolean = false;
    private bootObservers: Array<Subject<any>> = [];

    private readonly apiFacade: ApiFacade;
    private readonly authFacade: AuthFacade;
    private readonly changeLogFacade: ChangeLogFacade;
    private readonly _cordovaThumbnailService: CordovaThumbnailService;
    private readonly filesystemFacade: UniversalFilesystemService;
    private readonly _fileOpenerService: FileOpener;
    private readonly _fileTransferService: FileTransfer;
    private readonly _nativeFileService: NativeFile;
    private readonly networkFacade: NetworkFacade;
    private readonly objectsFacade: ObjectsFacade;
    private readonly _remoteThumbnailService: RemoteThumbnailService;
    private readonly schedulerFacade: SchedulerService;
    private readonly sessionFacade: SessionFacade;
    private readonly _shareService: ShareService;

    private readonly stateManagerFacade: StateManagerFacade;
    private readonly storeFacade: ObjectsStoreFacade;
    private readonly syncDownFacade: SyncDownFacade;
    private readonly syncUpFacade: SyncUpFacade;

    static getInstance(): VasAppFacade {
        return VasAppFacade._instance;
    }

    constructor(
        restService: VASRestService,
        storeService: VasStore,
        stateStorage: IStorage,
        changeLogStorage: IStorage,
        config: AppConfig,
        translateService: TranslateService,
        toastController: ToastController,
        loadingController: LoadingController,
        alertController: AlertController,
        popoverController: PopoverController,
        platformService: Platform,
        protected normalizationService: NormalizationService,
        filesystem: UniversalFilesystemService,
        appVersion: AppVersion,
        http: Http,
        ngZone: NgZone,
        protected errorHandlerService: ErrorHandler,
        fileTransferService: FileTransfer,
        fileOpenerService: FileOpener,
        nativeFileService: NativeFile,
        shareService: ShareService,
        protected _actions$: Actions,
        cordovaThumbnailService: CordovaThumbnailService,
        remoteThumbnailService: RemoteThumbnailService
    ) {
        super(
            config,
            translateService,
            toastController,
            loadingController,
            alertController,
            popoverController,
            platformService,
            appVersion,
            http
        );
        this.apiFacade = new ApiFacade(restService);
        this.authFacade = new AuthFacade(this, restService);
        this.changeLogFacade = new ChangeLogFacade(this, changeLogStorage);
        this.filesystemFacade = filesystem;
        this.networkFacade = new NetworkFacade(this, restService);
        this.objectsFacade = new ObjectsFacade(this, restService, storeService);
        this.schedulerFacade = new SchedulerService(this, ngZone);
        this.sessionFacade = new SessionFacade(this);
        this.stateManagerFacade = new StateManagerFacade(this, stateStorage, storeService);
        this.storeFacade = new ObjectsStoreFacade(this, storeService);
        this.syncDownFacade = new SyncDownFacade(this, restService, config);
        this.syncUpFacade = new SyncUpFacade(this, restService);
        this._fileTransferService = fileTransferService;
        this._fileOpenerService = fileOpenerService;
        this._nativeFileService = nativeFileService;
        this._shareService = shareService;
        this._cordovaThumbnailService = cordovaThumbnailService;
        this._remoteThumbnailService = remoteThumbnailService;

        VasAppFacade._instance = this;
    }

    get actions$(): Actions {
        return this._actions$;
    }

    get api(): ApiFacade {
        return this.apiFacade;
    }

    get auth(): AuthFacade {
        return this.authFacade;
    }

    get errorHandler(): ErrorHandler {
        return this.errorHandlerService;
    }

    get objects(): ObjectsFacade {
        return this.objectsFacade;
    }

    get changeLog(): ChangeLogFacade {
        return this.changeLogFacade;
    }

    get cordovaThumbnailService(): CordovaThumbnailService {
        return this._cordovaThumbnailService;
    }

    get filesystem(): UniversalFilesystemService {
        return this.filesystemFacade;
    }

    get fileOpenerService(): FileOpener {
        return this._fileOpenerService;
    }

    get fileTransferService(): FileTransfer {
        return this._fileTransferService;
    }

    get nativeFileService(): NativeFile {
        return this._nativeFileService;
    }

    get network(): NetworkFacade {
        return this.networkFacade;
    }

    get normalizer(): NormalizationService {
        return this.normalizationService;
    }

    get remoteThumbnailService(): RemoteThumbnailService {
        return this._remoteThumbnailService;
    }

    get session(): SessionFacade {
        return this.sessionFacade;
    }

    get shareService(): ShareService {
        return this._shareService;
    }

    get syncDown(): SyncDownFacade {
        return this.syncDownFacade;
    }

    get scheduler(): SchedulerService {
        return this.schedulerFacade;
    }

    get stateManager(): StateManagerFacade {
        return this.stateManagerFacade;
    }

    get store(): ObjectsStoreFacade {
        return this.storeFacade;
    }

    get syncUp(): SyncUpFacade {
        return this.syncUpFacade;
    }

    boot(): Observable<void> {

        if (this.hasBooted) {
            throw new Error('app has already been booted');
        }

        if (this.isBooting) {
            throw new Error('the app is booting');
        }

        this.isBooting = true;

        console.log('booting. environment is \'%s\'', ENV.name);

        this.triggerSystemEvent(new BootingSystemEvent());

        // perform boot steps
        return this.stateManager.rehydrate()
            .do(() => console.log('> rehydrated'))
            // ensure that we have values for the api server in the store
            .do(() => {

                this.store.select(ConfigQueries.getRestHost)
                    .take(1)
                    .subscribe(value => {

                        if (!value) {
                            this.store.dispatch(new fromConfig.SetRestHostAction(this.config.REST.HOST));
                        }
                    });

                this.store.select(ConfigQueries.getRestEndpoint)
                    .take(1)
                    .subscribe(value => {
                        if (!value) {
                            this.store.dispatch(new fromConfig.SetRestEndpointAction(this.config.REST.ENDPOINT));
                        }
                    });
            })
            .concatMap(() => this.network.waitForNetwork())
            .do(() => console.log('> got network'))
            .concatMap(() => this.network.refreshStatus$())
            .do(() => console.log('> api status refreshed'))
            .catch(error => {
                console.warn('> network is down. continuing', error);
                return of(undefined);
            })
            .do(() => this.session.boot())
            .do(() => console.log('> session is ready'))
            .do(() => {
                // if the app is online we will wipe all the cached jobs from
                // the store. This ensures any jobs that are not visible to the
                // user anymore are removed.
                this.network.isOnline()
                    .take(1)
                    .subscribe(online => {
                        if (online) {
                            return this.objects.job.store.wipeJobs();
                        }
                    });
            })
            .do(() => {
                this.hasBooted = true;
                this.isBooting = false;
                this.triggerSystemEvent(new BootedSystemEvent());
                this.initOnAfterLogin();
                console.log('> boot complete. triggering app ready');
                this.triggerAppReadyObserver();
            })
            .do(() => this.initScheduler())
            .do(() => this.scheduler.startScheduler())
            .do(() => console.log('> scheduler started'))
            .catch(error => of(undefined));
    }

    initOnAfterLogin(): void {
        super.initOnAfterLogin();
        this.syncDown.init();
        // synchronise the app after login

        this.getActiveUser()
            .take(1)
            .subscribe(user => {
                if (user) {
                    setTimeout(() => {
                        // this.triggerSystemEvent(new LoggedInEvent());
                    }, 100);
                }
            });
    }

    getActiveUser(): Observable<User> {
        return this.objects.user.store.getActive();
    }

    protected triggerAppReadyObserver(): void {
        this.bootObservers.forEach(o => {
            o.next(undefined);
            o.complete();
        });

        this.store.dispatch(new AppReadyAction());
    }

    triggerSystemEvent(event: Action): void {
        this.store.dispatch(event);
    }

    whenAppReady(): Observable<void> {

        if (this.hasBooted) {
            return of(undefined);
        }

        const observable = new Subject<any>();
        this.bootObservers.push(observable);
        return observable;
    }

    initScheduler(): void {

        const scheduleTasks = this.config.SCHEDULE.map(item => {
            return new ScheduledTask(
                item.id,
                this.getServiceByName(item.serviceName),
                item.fnName,
                item.args,
                item.priority,
                item.interval,
                item.timeout,
                item.deps,
                item.requirements);
        });

        this.scheduler.init(scheduleTasks);
    }

    /**
     * Convenience method that returns an observable emitting only once the
     * an action with type = actionType, or timeout milliseconds has elapsed.
     * Whichever happens first. The observable will only emit once.
     *
     * This method is useful if we wish to wait for a certain action to be
     * dispatched before proceeding e.g. a component may refresh its data after
     * the sync up has completed.
     * @param actionType {string} the type of the action to wait for
     * @param timeout {number} the number of ms to wait before timing out
     * @return {Observable<string>} the observable returns either "timeout" or
     * "action" depending on which even occurred triggered the emit.
     */
    onAction(actionType: string, timeout: number = 1000): Observable<string> {
        return Observable.combineLatest(
            of('timeout').debounceTime(timeout),
            this.actions$.ofType(actionType).map(() => 'action')
        )
            .map(value => value.find(v => !!v))
            .take(1);
    }

    protected getServiceByName(name: string): any {

        switch (name) {
            case 'VASRestService': {
                return this.api;
            }
            case 'SyncUpService': {
                return this.syncUp;
            }
            case 'SyncDownService': {
                return this.syncDown;
            }
            default:
                throw Error(`unknown service '${name}'`);
        }
    }

    dumpState(): void {
        this.store.select(state => state)
            .take(1)
            .subscribe(s => console.log(s));
    }
}



// WEBPACK FOOTER //
// ./src/vas/services/vas-app-facade.service.ts